import React, {useState} from 'react';
import {Marketer} from '../models/marketer.model';
import {UpdateMarketerPasswordRequest} from '../models/UpdateMarketer';
import Button from '../../../Presentation/components/ui/Button';
import AppInput from '../../../Presentation/components/ui/AppInput';
import Modal from '../../../Presentation/components/ui/Modal';
import {useMarketersUIStore} from '../store/marketersUIStore';

interface ChangePasswordFormProps {
  marketer: Marketer;
  onSubmit: (passwordData: UpdateMarketerPasswordRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  open: boolean;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  marketer,
  onSubmit,
  onCancel,
  loading = false,
  open
}) => {
  const {
    changePasswordData: formData, changePasswordErrors: errors, changePasswordShowPassword: showPassword,
    setChangePasswordData: setFormData, setChangePasswordErrors: setErrors, setChangePasswordShowPassword: setShowPassword, resetChangePasswordForm
  } = useMarketersUIStore();

  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Password validation
    if (!formData.newPassword?.trim()) {
      newErrors.newPassword = 'كلمة المرور الجديدة مطلوبة';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'كلمة المرور يجب أن تكون 8 أحرف على الأقل';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'كلمة المرور يجب أن تحتوي على حرف صغير وكبير ورقم';
    }

    // Confirm password validation
    if (!confirmPassword?.trim()) {
      newErrors.confirmPassword = 'تأكيد كلمة المرور مطلوب';
    } else if (confirmPassword !== formData.newPassword) {
      newErrors.confirmPassword = 'كلمة المرور وتأكيدها غير متطابقين';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      // Reset form after successful submission
      resetChangePasswordForm();
      setConfirmPassword('');
      setShowConfirmPassword(false);
    } catch (error) {
    }
  };

  const handleInputChange = (field: keyof UpdateMarketerPasswordRequest, value: string) => {
    setFormData({[field]: value});
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({...errors, [field]: ''});
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (errors.confirmPassword) {
      setErrors({...errors, confirmPassword: ''});
    }
  };

  return (
    <Modal open={open} onClose={onCancel} title="تغيير كلمة مرور المسوق">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* معلومات المسوق */}
        <div className="bg-brand-50 p-4 rounded-lg">
          <h4 className="font-medium text-brand-800 mb-2">معلومات المسوق</h4>
          <div className="text-sm text-brand-700">
            <p><span className="font-medium">الاسم:</span> {marketer.fullName || marketer.name}</p>
            <p><span className="font-medium">اسم المستخدم:</span> {marketer.username}</p>
            <p><span className="font-medium">الدور:</span> {
              typeof marketer.role === 'string'
                ? marketer.role
                : (marketer.role as any)?.name || 'غير محدد'
            }</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* كلمة المرور الجديدة */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              كلمة المرور الجديدة *
            </label>
            <div className="relative">
              <AppInput
                type={showPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                placeholder="أدخل كلمة المرور الجديدة"
                error={errors.newPassword}
                className="w-full pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              يجب أن تحتوي على 8 أحرف على الأقل، حرف صغير، حرف كبير، ورقم
            </p>
          </div>

          {/* تأكيد كلمة المرور */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              تأكيد كلمة المرور *
            </label>
            <div className="relative">
              <AppInput
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                placeholder="أعد إدخال كلمة المرور الجديدة"
                error={errors.confirmPassword}
                className="w-full pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* تحذير */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="mr-3">
              <h3 className="text-sm font-medium text-yellow-800">تحذير</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>سيتم تغيير كلمة مرور المسوق. تأكد من إبلاغ المسوق بكلمة المرور الجديدة.</p>
              </div>
            </div>
          </div>
        </div>

        {/* أزرار الإجراءات */}
        <div className="flex gap-4 justify-end pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="px-6"
          >
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={loading}
            disabled={loading}
            className="px-6"
          >
            {loading ? 'جاري التغيير...' : 'تغيير كلمة المرور'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ChangePasswordForm;
